"use client";

import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    ArrowLeft,
    PencilSimple,
    Trash,
    Package,
    ArrowsClockwise,
    Cube,
    CurrencyDollar,
    TrendUp,
    TrendDown,
    Storefront,
    Tag,
    Barcode,
    Calendar,
    ShoppingCart,
    ChartLine,
    ClockCounterClockwise,
    CaretUp,
    CaretDown,
    Printer,
} from "@phosphor-icons/react";
import { Loader2, AlertTriangle, PackageX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/components/ui/use-toast";
import { getProduct, deleteProduct, reset } from "@/state/products/product.slice";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

// ============================================
// LOADING SPINNER COMPONENT
// ============================================
const LoadingSpinner = ({ size = "default", text }) => (
    <div className="flex items-center justify-center gap-2">
        <Loader2
            className={`animate-spin ${size === "sm" ? "h-4 w-4" : size === "lg" ? "h-8 w-8" : "h-6 w-6"
                }`}
        />
        {text && <span className="text-sm text-muted-foreground">{text}</span>}
    </div>
);

// ============================================
// STOCK BADGE COMPONENT
// ============================================
const StockBadge = ({ quantity, alertQuantity = 5 }) => {
    if (quantity === 0) {
        return (
            <Badge variant="destructive" className="flex items-center gap-1">
                <PackageX className="h-3 w-3" />
                Out of Stock
            </Badge>
        );
    }
    if (quantity <= alertQuantity) {
        return (
            <Badge variant="warning" className="flex items-center gap-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800">
                <AlertTriangle className="h-3 w-3" />
                Low Stock ({quantity})
            </Badge>
        );
    }
    return (
        <Badge variant="success" className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800">
            {quantity} in stock
        </Badge>
    );
};

// ============================================
// STOCK ADJUSTMENT DIALOG
// ============================================
const StockAdjustmentDialog = ({ product, open, onClose, onSubmit, isLoading }) => {
    const [adjustment, setAdjustment] = useState(0);
    const [reason, setReason] = useState("");
    const [adjustmentType, setAdjustmentType] = useState("add");

    const handleSubmit = (e) => {
        e.preventDefault();
        const finalAdjustment = adjustmentType === "add" ? Math.abs(adjustment) : -Math.abs(adjustment);
        onSubmit(product?.id, finalAdjustment, reason);
        setAdjustment(0);
        setReason("");
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Adjust Stock - {product?.title}</DialogTitle>
                    <DialogDescription>
                        Current stock: {product?.stockQuantity || 0} units
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label>Adjustment Type</Label>
                        <Select value={adjustmentType} onValueChange={setAdjustmentType}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="add">Add Stock</SelectItem>
                                <SelectItem value="remove">Remove Stock</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="adjustment">Quantity</Label>
                        <Input
                            id="adjustment"
                            type="number"
                            min="1"
                            value={adjustment}
                            onChange={(e) => setAdjustment(parseInt(e.target.value) || 0)}
                            placeholder="Enter quantity"
                            required
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="reason">Reason (Optional)</Label>
                        <Input
                            id="reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. New shipment, Damaged goods"
                        />
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isLoading || adjustment <= 0}>
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Adjusting...
                                </>
                            ) : (
                                `${adjustmentType === "add" ? "Add" : "Remove"} Stock`
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

// ============================================
// INFO ROW COMPONENT
// ============================================
const InfoRow = ({ icon: Icon, label, value, valueClassName = "" }) => (
    <div className="flex items-start gap-3 py-2">
        <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg">
            <Icon className="h-4 w-4 text-slate-600 dark:text-slate-400" />
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
            <p className={`font-medium text-slate-900 dark:text-slate-100 ${valueClassName}`}>
                {value || "—"}
            </p>
        </div>
    </div>
);

// ============================================
// ACTIVITY ITEM COMPONENT
// ============================================
const ActivityItem = ({ type, quantity, reason, date, user }) => {
    const isPositive = quantity > 0;
    const Icon = isPositive ? CaretUp : CaretDown;
    const colorClass = isPositive
        ? "text-green-600 bg-green-50 dark:bg-green-900/30"
        : "text-red-600 bg-red-50 dark:bg-red-900/30";

    return (
        <div className="flex items-center gap-4 py-3 border-b border-slate-100 dark:border-slate-800 last:border-0">
            <div className={`p-2 rounded-lg ${colorClass}`}>
                <Icon className="h-4 w-4" weight="bold" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 dark:text-slate-100">
                    {type || "Stock Adjustment"}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                    {reason || "Manual adjustment"}
                </p>
            </div>
            <div className="text-right">
                <p className={`font-bold ${isPositive ? "text-green-600" : "text-red-600"}`}>
                    {isPositive ? "+" : ""}{quantity}
                </p>
                <p className="text-xs text-slate-400">
                    {new Date(date).toLocaleDateString()}
                </p>
            </div>
        </div>
    );
};

// ============================================
// MAIN PRODUCT DETAILS PAGE
// ============================================
export default function ProductDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { toast } = useToast();

    const { product, isLoading, success, error, message } = useSelector(
        (state) => state.product
    );

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showStockDialog, setShowStockDialog] = useState(false);
    const [adjustingStock, setAdjustingStock] = useState(false);
    const [stockHistory, setStockHistory] = useState([]);
    const [salesData, setSalesData] = useState([]);
    const [priceHistory, setPriceHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    // Fetch product data
    useEffect(() => {
        dispatch(getProduct(id));
    }, [dispatch, id]);

    // Fetch stock history
    useEffect(() => {
        const fetchHistory = async () => {
            setLoadingHistory(true);
            try {
                const res = await axios.get(`${API_URL}products/${id}/history`, {
                    withCredentials: true,
                });
                if (res.data?.data) {
                    const timeline = res.data.data.timeline || [];

                    // Separate stock events from sales events
                    const stockEvents = timeline
                        .filter(item => item.type === 'stock')
                        .map(item => ({
                            type: item.subType || 'adjustment',
                            quantity: item.change || 0,
                            reason: item.reason,
                            createdAt: item.date,
                            user: item.user
                        }));

                    const salesEvents = timeline
                        .filter(item => item.type === 'sale')
                        .map(item => ({
                            date: item.date,
                            invoice: item.orderNumber,
                            quantity: item.quantity || 0,
                            price: item.price || 0,
                            total: item.total || 0,
                            customer: item.customer
                        }));

                    const priceEvents = timeline
                        .filter(item => item.type === 'price')
                        .map(item => ({
                            date: item.date,
                            subType: item.subType,
                            oldPrice: item.oldPrice || 0,
                            newPrice: item.newPrice || 0,
                            reason: item.reason,
                            user: item.user
                        }));

                    setStockHistory(stockEvents);
                    setSalesData(salesEvents);
                    setPriceHistory(priceEvents);
                }
            } catch (err) {
                console.error("Failed to fetch history:", err);
            } finally {
                setLoadingHistory(false);
            }
        };
        if (id) fetchHistory();
    }, [id]);

    // Handle success/error
    useEffect(() => {
        if (success && message) {
            toast({
                title: "✓ Success",
                description: message,
                className: "bg-green-50 dark:bg-green-900/30 border-green-200",
            });
            dispatch(reset());
            if (message.includes("deleted")) {
                navigate("/dashboard/products");
            }
        }
        if (error) {
            toast({
                title: "✕ Error",
                description: typeof error === "string" ? error : "An error occurred",
                variant: "destructive",
            });
            dispatch(reset());
        }
    }, [success, error, message, toast, dispatch, navigate]);

    const productData = product?.data;
    const formatCurrency = (amount) => `GHS ${parseFloat(amount || 0).toFixed(2)}`;

    const handleDelete = async () => {
        setShowDeleteDialog(false);
        dispatch(deleteProduct(id));
    };

    const handleStockAdjustment = async (productId, adjustment, reason) => {
        setAdjustingStock(true);
        try {
            await axios.patch(
                `${API_URL}products/${productId}/stock`,
                { adjustment, reason },
                { withCredentials: true }
            );
            toast({
                title: "✓ Success",
                description: `Stock ${adjustment > 0 ? "added" : "removed"} successfully`,
                className: "bg-green-50 dark:bg-green-900/30 border-green-200",
            });
            setShowStockDialog(false);
            dispatch(getProduct(id));
        } catch (err) {
            toast({
                title: "✕ Error",
                description: err.response?.data?.message || "Failed to adjust stock",
                variant: "destructive",
            });
        } finally {
            setAdjustingStock(false);
        }
    };

    // Profit margin calculation
    const profitMargin = useMemo(() => {
        if (!productData) return 0;
        const cost = parseFloat(productData.buying_price) || 0;
        const retail = parseFloat(productData.current_price) || 0;
        if (cost === 0) return 0;
        return ((retail - cost) / cost * 100).toFixed(1);
    }, [productData]);

    if (isLoading && !productData) {
        return (
            <div className="flex items-center justify-center h-96">
                <LoadingSpinner size="lg" text="Loading product details..." />
            </div>
        );
    }

    if (!productData) {
        return (
            <div className="flex flex-col items-center justify-center h-96 gap-4">
                <Package className="h-16 w-16 text-slate-300" />
                <h2 className="text-xl font-semibold text-slate-600">Product not found</h2>
                <Button variant="outline" onClick={() => navigate(-1)}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Go Back
                </Button>
            </div>
        );
    }

    return (
        <div className="mx-auto p-4 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div className="flex items-center gap-4">
                        {productData.feature_image ? (
                            <img
                                src={productData.feature_image}
                                alt={productData.title}
                                className="w-16 h-16 object-cover rounded-lg border"
                            />
                        ) : (
                            <div className="w-16 h-16 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 rounded-lg flex items-center justify-center">
                                <Package className="h-8 w-8 text-slate-400" />
                            </div>
                        )}
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                                {productData.title}
                            </h1>
                            <div className="flex items-center gap-2 mt-1">
                                <Badge variant={productData.status === 1 ? "success" : "secondary"}>
                                    {productData.status === 1 ? "Active" : "Inactive"}
                                </Badge>
                                <StockBadge
                                    quantity={productData.stockQuantity}
                                    alertQuantity={productData.alertQuantity}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 flex-wrap">
                    <Button variant="outline" onClick={() => setShowStockDialog(true)}>
                        <ArrowsClockwise className="mr-2 h-4 w-4" />
                        Adjust Stock
                    </Button>
                    <Button variant="outline" onClick={() => window.print()}>
                        <Printer className="mr-2 h-4 w-4" />
                        Print
                    </Button>
                    <Button
                        onClick={() => navigate(`/dashboard/products/edit/${id}`)}
                    >
                        <PencilSimple className="mr-2 h-4 w-4" />
                        Edit Product
                    </Button>
                    <Button variant="destructive" onClick={() => setShowDeleteDialog(true)}>
                        <Trash className="mr-2 h-4 w-4" />
                        Delete
                    </Button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-800">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-200 dark:bg-blue-800 rounded-lg">
                                <CurrencyDollar className="h-5 w-5 text-blue-600 dark:text-blue-300" />
                            </div>
                            <div>
                                <p className="text-xs text-blue-600 dark:text-blue-400">Retail Price</p>
                                <p className="text-xl font-bold text-blue-700 dark:text-blue-300">
                                    {formatCurrency(productData.current_price)}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-800">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-200 dark:bg-purple-800 rounded-lg">
                                <Storefront className="h-5 w-5 text-purple-600 dark:text-purple-300" />
                            </div>
                            <div>
                                <p className="text-xs text-purple-600 dark:text-purple-400">Wholesale</p>
                                <p className="text-xl font-bold text-purple-700 dark:text-purple-300">
                                    {formatCurrency(productData.wholesale_price)}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 border-emerald-200 dark:border-emerald-800">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-emerald-200 dark:bg-emerald-800 rounded-lg">
                                <TrendUp className="h-5 w-5 text-emerald-600 dark:text-emerald-300" />
                            </div>
                            <div>
                                <p className="text-xs text-emerald-600 dark:text-emerald-400">Profit Margin</p>
                                <p className="text-xl font-bold text-emerald-700 dark:text-emerald-300">
                                    {profitMargin}%
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border-orange-200 dark:border-orange-800">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-orange-200 dark:bg-orange-800 rounded-lg">
                                <Cube className="h-5 w-5 text-orange-600 dark:text-orange-300" />
                            </div>
                            <div>
                                <p className="text-xs text-orange-600 dark:text-orange-400">Stock Value</p>
                                <p className="text-xl font-bold text-orange-700 dark:text-orange-300">
                                    {formatCurrency((productData.stockQuantity || 0) * (productData.buying_price || 0))}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="overview" className="w-full">
                <TabsList className="w-full justify-start border-b rounded-none h-auto p-0 bg-transparent">
                    <TabsTrigger
                        value="overview"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3"
                    >
                        <Package className="mr-2 h-4 w-4" />
                        Overview
                    </TabsTrigger>
                    <TabsTrigger
                        value="stock"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3"
                    >
                        <ClockCounterClockwise className="mr-2 h-4 w-4" />
                        Stock Activity
                    </TabsTrigger>
                    <TabsTrigger
                        value="sales"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3"
                    >
                        <ShoppingCart className="mr-2 h-4 w-4" />
                        Sales History
                    </TabsTrigger>
                    <TabsTrigger
                        value="analytics"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3"
                    >
                        <ChartLine className="mr-2 h-4 w-4" />
                        Analytics
                    </TabsTrigger>
                    <TabsTrigger
                        value="prices"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3"
                    >
                        <CurrencyDollar className="mr-2 h-4 w-4" />
                        Price History
                    </TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="mt-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Product Information */}
                        <Card className="lg:col-span-2">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Package className="h-5 w-5" />
                                    Product Information
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <InfoRow icon={Tag} label="Product Name" value={productData.title} />
                                    <InfoRow icon={Barcode} label="SKU" value={productData.sku} />
                                    <InfoRow icon={Cube} label="Category" value={productData.Category?.name} />
                                    <InfoRow icon={Tag} label="Brand" value={productData.Brand?.name} />
                                    <InfoRow icon={Storefront} label="Shop" value={productData.shop?.name} />
                                    <InfoRow icon={Package} label="Unit" value={productData.Unit?.name} />
                                    <InfoRow icon={Barcode} label="Barcode Type" value={productData.barcodeType} />
                                    <InfoRow
                                        icon={Calendar}
                                        label="Created"
                                        value={productData.createdAt ? new Date(productData.createdAt).toLocaleDateString() : "—"}
                                    />
                                </div>
                                {productData.description && (
                                    <div className="mt-4 pt-4 border-t">
                                        <p className="text-sm text-slate-500 dark:text-slate-400">Description</p>
                                        <p className="mt-1 text-slate-700 dark:text-slate-300">{productData.description}</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Pricing & Stock */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <CurrencyDollar className="h-5 w-5" />
                                    Pricing & Stock
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-slate-500">Buying Price</span>
                                        <span className="font-medium">{formatCurrency(productData.buying_price)}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-slate-500">Retail Price</span>
                                        <span className="font-bold text-lg text-blue-600">{formatCurrency(productData.current_price)}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-slate-500">Wholesale Price</span>
                                        <span className="font-medium text-purple-600">{formatCurrency(productData.wholesale_price)}</span>
                                    </div>
                                    <div className="h-px bg-slate-200 dark:bg-slate-700" />
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-slate-500">Profit per Unit</span>
                                        <span className="font-bold text-green-600">
                                            {formatCurrency((productData.current_price || 0) - (productData.buying_price || 0))}
                                        </span>
                                    </div>
                                </div>

                                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-slate-500">Current Stock</span>
                                        <span className="font-bold text-lg">{productData.stockQuantity || 0} units</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-slate-500">Alert Quantity</span>
                                        <span className="font-medium text-orange-500">{productData.alertQuantity || 5} units</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-slate-500">Stock Status</span>
                                        <StockBadge quantity={productData.stockQuantity} alertQuantity={productData.alertQuantity} />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Stock Activity Tab */}
                <TabsContent value="stock" className="mt-6">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="flex items-center gap-2">
                                <ClockCounterClockwise className="h-5 w-5" />
                                Stock Movements
                            </CardTitle>
                            <Button variant="outline" size="sm" onClick={() => setShowStockDialog(true)}>
                                <ArrowsClockwise className="mr-2 h-4 w-4" />
                                Adjust Stock
                            </Button>
                        </CardHeader>
                        <CardContent>
                            {loadingHistory ? (
                                <div className="flex justify-center py-8">
                                    <LoadingSpinner text="Loading history..." />
                                </div>
                            ) : stockHistory.length > 0 ? (
                                <div className="max-h-96 overflow-y-auto">
                                    {stockHistory.map((item, index) => (
                                        <ActivityItem
                                            key={index}
                                            type={item.type}
                                            quantity={item.quantity}
                                            reason={item.reason}
                                            date={item.createdAt}
                                            user={item.user}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <ClockCounterClockwise className="mx-auto h-12 w-12 text-slate-300 mb-4" />
                                    <p className="text-slate-500">No stock movements recorded yet</p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="mt-4"
                                        onClick={() => setShowStockDialog(true)}
                                    >
                                        Make First Adjustment
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Sales History Tab */}
                <TabsContent value="sales" className="mt-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <ShoppingCart className="h-5 w-5" />
                                Sales History
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {loadingHistory ? (
                                <div className="flex justify-center py-8">
                                    <LoadingSpinner text="Loading sales..." />
                                </div>
                            ) : salesData.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b">
                                                <th className="text-left py-3 px-4 font-medium text-slate-500">Date</th>
                                                <th className="text-left py-3 px-4 font-medium text-slate-500">Invoice</th>
                                                <th className="text-right py-3 px-4 font-medium text-slate-500">Quantity</th>
                                                <th className="text-right py-3 px-4 font-medium text-slate-500">Price</th>
                                                <th className="text-right py-3 px-4 font-medium text-slate-500">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {salesData.map((sale, index) => (
                                                <tr key={index} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800">
                                                    <td className="py-3 px-4">{new Date(sale.date).toLocaleDateString()}</td>
                                                    <td className="py-3 px-4">{sale.invoice || "—"}</td>
                                                    <td className="py-3 px-4 text-right">{sale.quantity}</td>
                                                    <td className="py-3 px-4 text-right">{formatCurrency(sale.price)}</td>
                                                    <td className="py-3 px-4 text-right font-medium">{formatCurrency(sale.total)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <ShoppingCart className="mx-auto h-12 w-12 text-slate-300 mb-4" />
                                    <p className="text-slate-500">No sales recorded for this product</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Analytics Tab */}
                <TabsContent value="analytics" className="mt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <TrendUp className="h-5 w-5" />
                                    Performance Summary
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                        <span className="text-sm text-slate-500">Total Units Sold</span>
                                        <span className="font-bold text-lg">
                                            {salesData.reduce((sum, s) => sum + (s.quantity || 0), 0)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                        <span className="text-sm text-slate-500">Total Revenue</span>
                                        <span className="font-bold text-lg text-green-600">
                                            {formatCurrency(salesData.reduce((sum, s) => sum + (s.total || 0), 0))}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                        <span className="text-sm text-slate-500">Average Sale Value</span>
                                        <span className="font-bold text-lg">
                                            {formatCurrency(
                                                salesData.length > 0
                                                    ? salesData.reduce((sum, s) => sum + (s.total || 0), 0) / salesData.length
                                                    : 0
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <ChartLine className="h-5 w-5" />
                                    Stock Insights
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                        <span className="text-sm text-slate-500">Stock Adjustments</span>
                                        <span className="font-bold text-lg">{stockHistory.length}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                        <span className="text-sm text-slate-500">Net Stock Change</span>
                                        <span className={`font-bold text-lg ${stockHistory.reduce((sum, h) => sum + (h.quantity || 0), 0) >= 0 ? "text-green-600" : "text-red-600"}`}>
                                            {stockHistory.reduce((sum, h) => sum + (h.quantity || 0), 0)} units
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                        <span className="text-sm text-slate-500">Days in Stock</span>
                                        <span className="font-bold text-lg">
                                            {productData.createdAt
                                                ? Math.floor((new Date() - new Date(productData.createdAt)) / (1000 * 60 * 60 * 24))
                                                : 0}
                                        </span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Price History Tab */}
                <TabsContent value="prices" className="mt-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <CurrencyDollar className="h-5 w-5" />
                                Price Change History
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {loadingHistory ? (
                                <div className="flex justify-center py-8">
                                    <LoadingSpinner text="Loading price history..." />
                                </div>
                            ) : priceHistory.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b">
                                                <th className="text-left py-3 px-4 font-medium text-slate-500">Date</th>
                                                <th className="text-left py-3 px-4 font-medium text-slate-500">Price Type</th>
                                                <th className="text-right py-3 px-4 font-medium text-slate-500">Old Price</th>
                                                <th className="text-right py-3 px-4 font-medium text-slate-500">New Price</th>
                                                <th className="text-right py-3 px-4 font-medium text-slate-500">Change</th>
                                                <th className="text-left py-3 px-4 font-medium text-slate-500">Changed By</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {priceHistory.map((ph, index) => {
                                                const priceTypeLabels = {
                                                    'buying_price': 'Buying Price',
                                                    'current_price': 'Retail Price',
                                                    'wholesale_price': 'Wholesale Price'
                                                };
                                                const change = ph.newPrice - ph.oldPrice;
                                                const changePercent = ph.oldPrice > 0 ? ((change / ph.oldPrice) * 100).toFixed(1) : 0;
                                                return (
                                                    <tr key={index} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800">
                                                        <td className="py-3 px-4">{new Date(ph.date).toLocaleDateString()}</td>
                                                        <td className="py-3 px-4">
                                                            <Badge variant="outline">{priceTypeLabels[ph.subType] || ph.subType}</Badge>
                                                        </td>
                                                        <td className="py-3 px-4 text-right text-slate-500">{formatCurrency(ph.oldPrice)}</td>
                                                        <td className="py-3 px-4 text-right font-medium">{formatCurrency(ph.newPrice)}</td>
                                                        <td className={`py-3 px-4 text-right font-medium ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                            {change >= 0 ? '+' : ''}{formatCurrency(change)} ({changePercent}%)
                                                        </td>
                                                        <td className="py-3 px-4">{ph.user || '—'}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <CurrencyDollar className="mx-auto h-12 w-12 text-slate-300 mb-4" />
                                    <p className="text-slate-500">No price changes recorded yet</p>
                                    <p className="text-sm text-slate-400 mt-1">Price changes will be tracked when you edit product prices</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Stock Adjustment Dialog */}
            <StockAdjustmentDialog
                product={productData}
                open={showStockDialog}
                onClose={() => setShowStockDialog(false)}
                onSubmit={handleStockAdjustment}
                isLoading={adjustingStock}
            />

            {/* Delete Confirmation */}
            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Product</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete "{productData.title}"? This action cannot
                            be undone and will also remove associated stock records.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
